import type { CSSProperties, ElementType, FocusEvent, ReactNode } from 'react';
import { useEditState, useSectionScope } from './context.js';

/**
 * Slots — how a component exposes its resolved field values and, in edit
 * mode, becomes the inline editing surface. The runtime owns the DOM while
 * the user types (contentEditable, uncontrolled); the value commits to the
 * document as a literal override on blur via a `slot-edit` message.
 */

export interface TextSlotProps {
  name: string;
  as?: ElementType;
  className?: string;
  style?: CSSProperties;
}

function useSlotValue(name: string) {
  const { emit } = useEditState();
  const { sectionKey, sectionId, props } = useSectionScope();
  const commit = (prop: string, value: string) =>
    emit({ type: 'slot-edit', blockId: sectionId, blockKey: sectionKey, prop, value });
  return { value: props[name], commit };
}

function useSlot(name: string) {
  const { editMode } = useEditState();
  return { editMode, ...useSlotValue(name) };
}

/** Plain-text field: rendered as text, edited in place as text. */
export function TextSlot({ name, as: Tag = 'span', className, style }: TextSlotProps) {
  const { editMode, value, commit } = useSlot(name);
  const text = value === null || value === undefined ? '' : String(value);
  if (!editMode) {
    return <Tag className={className} style={style} data-slot={name}>{text}</Tag>;
  }
  return (
    <Tag
      className={className}
      style={style}
      data-slot={name}
      contentEditable
      suppressContentEditableWarning
      onBlur={(e: FocusEvent<HTMLElement>) => commit(name, e.currentTarget.textContent ?? '')}
    >
      {text}
    </Tag>
  );
}

export interface RichSlotProps {
  name: string;
  as?: ElementType;
  className?: string;
  style?: CSSProperties;
}

/**
 * Rich-text (HTML string) field. The HTML is document content authored in
 * this product. In edit mode the app-supplied rich editor (e.g. TipTap)
 * takes over; plain contentEditable is the dependency-free fallback.
 */
export function RichSlot({ name, as: Tag = 'div', className, style }: RichSlotProps) {
  const { editMode, richEditor: RichEditor } = useEditState();
  const { value, commit } = useSlotValue(name);
  const html = value === null || value === undefined ? '' : String(value);
  if (!editMode) {
    return (
      <Tag
        className={className}
        style={style}
        data-slot={name}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }
  if (RichEditor) {
    return (
      <Tag className={className} style={style} data-slot={name}>
        <RichEditor value={html} onCommit={(next) => commit(name, next)} />
      </Tag>
    );
  }
  return (
    <Tag
      className={className}
      style={style}
      data-slot={name}
      contentEditable
      suppressContentEditableWarning
      onBlur={(e: FocusEvent<HTMLElement>) => commit(name, e.currentTarget.innerHTML)}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export interface ItemTextSlotProps {
  /** the list prop name on the block */
  list: string;
  index: number;
  /** the field inside the item */
  name: string;
  /** the resolved item value to display */
  value: unknown;
  as?: ElementType;
  className?: string;
  style?: CSSProperties;
}

/** Inline-editable text for one field of one list item (e.g. an FAQ answer). */
export function ItemTextSlot({ list, index, name, value, as: Tag = 'span', className, style }: ItemTextSlotProps) {
  const { editMode, emit } = useEditState();
  const { sectionKey, sectionId, itemOffset } = useSectionScope();
  // A paginated slice renders items with local indices; the real data index is
  // offset by where the slice starts.
  const realIndex = index + (itemOffset ?? 0);
  const text = value === null || value === undefined ? '' : String(value);
  const slotId = `${list}.${realIndex}.${name}`;
  if (!editMode) {
    return <Tag className={className} style={style} data-slot={slotId}>{text}</Tag>;
  }
  return (
    <Tag
      className={className}
      style={style}
      data-slot={slotId}
      contentEditable
      suppressContentEditableWarning
      onBlur={(e: FocusEvent<HTMLElement>) =>
        emit({
          type: 'slot-edit',
          blockId: sectionId,
          blockKey: sectionKey,
          prop: list,
          itemPath: `${realIndex}.${name}`,
          value: e.currentTarget.textContent ?? '',
        })
      }
    >
      {text}
    </Tag>
  );
}

export interface ListItemControlsProps {
  list: string;
  index: number;
  /** blank item inserted by the + control */
  template: Record<string, unknown>;
}

/** Hover controls on a list item: add one below, remove this one. */
export function ListItemControls({ list, index, template }: ListItemControlsProps) {
  const { editMode, emit } = useEditState();
  const { sectionKey, sectionId, itemOffset } = useSectionScope();
  if (!editMode) return null;
  const realIndex = index + (itemOffset ?? 0);
  const fire = (op: 'insert' | 'remove', idx: number, item?: Record<string, unknown>) =>
    emit({
      type: 'list-edit',
      blockId: sectionId,
      blockKey: sectionKey,
      prop: list,
      op,
      index: idx,
      ...(item ? { item } : {}),
    });
  return (
    <span className="dg-item-controls" contentEditable={false}>
      <button
        type="button"
        title="Add item below"
        aria-label="Add item below"
        onClick={(e) => {
          e.stopPropagation();
          fire('insert', realIndex + 1, template);
        }}
      >
        +
      </button>
      <button
        type="button"
        title="Remove item"
        aria-label="Remove item"
        onClick={(e) => {
          e.stopPropagation();
          fire('remove', realIndex);
        }}
      >
        ×
      </button>
    </span>
  );
}

export interface EachSlotProps<T = unknown> {
  name: string;
  children: (item: T, index: number) => ReactNode;
  /** rendered when the list is missing or empty */
  fallback?: ReactNode;
}

/** List field: maps the resolved array through a render prop. */
export function EachSlot<T = unknown>({ name, children, fallback = null }: EachSlotProps<T>) {
  const { props } = useSectionScope();
  const items = props[name];
  if (!Array.isArray(items) || items.length === 0) return <>{fallback}</>;
  return <>{items.map((item, i) => children(item as T, i))}</>;
}
